/**
 * TopAdvertisersWidgetPage — ranked top-advertisers report, ported from pre-nuke
 * `apps/dash-legacy/src/routes/_authed/advertisers/index.tsx` with its gradient
 * H1, zinc borders, Tailwind-only surface. Adds date-range + top-N selector and
 * an SVG sparkline per row. Reads GET /api/v1/top-advertisers-widget.
 */
import { useMemo, useState } from "react";
import { useTopAdvertisersWidgetList } from "./hooks";
import type { TopAdvertiserRow, TopN } from "./api";

const CARD = "rounded-lg border border-zinc-200 bg-white p-5 shadow-sm";
const TH =
	"px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-zinc-500";
const TD = "px-3 py-2 text-sm text-zinc-900";
const CTRL =
	"rounded-md border border-zinc-200 bg-white px-3 py-2 text-sm text-zinc-900 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-200";

const TOP_OPTIONS: TopN[] = [5, 10, 25];

function todayISO(): string {
	return new Date().toISOString().slice(0, 10);
}
function daysAgoISO(n: number): string {
	const d = new Date();
	d.setUTCDate(d.getUTCDate() - n);
	return d.toISOString().slice(0, 10);
}

function fmtMoney(n: number): string {
	return n.toLocaleString(undefined, {
		style: "currency",
		currency: "USD",
		maximumFractionDigits: 0,
	});
}
function fmtInt(n: number): string {
	return n.toLocaleString();
}
function fmtPct(n: number): string {
	return `${(n * 100).toFixed(2)}%`;
}
function fmtRoas(n: number): string {
	return `${n.toFixed(2)}x`;
}

interface SparklineProps {
	values: number[];
	width?: number;
	height?: number;
}

function Sparkline({ values, width = 72, height = 20 }: SparklineProps) {
	if (!values || values.length < 2) {
		return (
			<svg
				role="img"
				aria-label="trend"
				width={width}
				height={height}
				data-testid="top-advertisers-widget-sparkline-empty"
			/>
		);
	}
	const min = Math.min(...values);
	const max = Math.max(...values);
	const span = max - min || 1;
	const step = values.length > 1 ? width / (values.length - 1) : width;
	const points = values
		.map((v, i) => {
			const x = i * step;
			const y = height - ((v - min) / span) * height;
			return `${x.toFixed(1)},${y.toFixed(1)}`;
		})
		.join(" ");
	return (
		<svg
			role="img"
			aria-label="trend"
			width={width}
			height={height}
			viewBox={`0 0 ${width} ${height}`}
			data-testid="top-advertisers-widget-sparkline"
		>
			<polyline
				fill="none"
				stroke="#059669"
				strokeWidth={1.5}
				strokeLinecap="round"
				strokeLinejoin="round"
				points={points}
			/>
		</svg>
	);
}

export function TopAdvertisersWidgetPage() {
	const [from, setFrom] = useState<string>(daysAgoISO(30));
	const [to, setTo] = useState<string>(todayISO());
	const [top, setTop] = useState<TopN>(10);

	const params = useMemo(() => ({ from, to, top }), [from, to, top]);
	const listQuery = useTopAdvertisersWidgetList(params);
	const rawItems: TopAdvertiserRow[] = listQuery.data?.items ?? [];
	const items = useMemo(() => {
		return [...rawItems]
			.sort((a, b) => (a.rank ?? 0) - (b.rank ?? 0))
			.slice(0, top);
	}, [rawItems, top]);

	return (
		<main
			data-testid="top-advertisers-widget-page"
			className="mx-auto flex w-full max-w-6xl flex-col gap-8 px-6 py-10"
		>
			<header className="flex flex-col gap-2">
				<p className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
					Report
				</p>
				<h1 className="bg-gradient-to-r from-emerald-600 to-sky-600 bg-clip-text text-3xl font-semibold text-transparent">
					Top Advertisers
				</h1>
				<p className="text-sm text-zinc-500">
					Ranked advertisers by spend for the selected date range.
				</p>
			</header>

			<section
				className="flex flex-wrap items-end gap-3 rounded-md border border-zinc-200 bg-white p-3"
				aria-label="Top advertisers filters"
			>
				<label className="flex flex-col gap-1 text-xs font-medium uppercase tracking-wider text-zinc-500">
					From
					<input
						type="date"
						value={from}
						onChange={(e) => setFrom(e.target.value)}
						className={CTRL}
						data-testid="top-advertisers-widget-from"
					/>
				</label>
				<label className="flex flex-col gap-1 text-xs font-medium uppercase tracking-wider text-zinc-500">
					To
					<input
						type="date"
						value={to}
						onChange={(e) => setTo(e.target.value)}
						className={CTRL}
						data-testid="top-advertisers-widget-to"
					/>
				</label>
				<label className="flex flex-col gap-1 text-xs font-medium uppercase tracking-wider text-zinc-500">
					Top N
					<select
						value={top}
						onChange={(e) => setTop(Number(e.target.value) as TopN)}
						className={CTRL}
						data-testid="top-advertisers-widget-top"
					>
						{TOP_OPTIONS.map((n) => (
							<option key={n} value={n}>
								Top {n}
							</option>
						))}
					</select>
				</label>
			</section>

			<section className={CARD} aria-label="Top advertisers table">
				{listQuery.isLoading ? (
					<p
						className="text-sm text-zinc-500"
						data-testid="top-advertisers-widget-loading"
					>
						Loading.
					</p>
				) : listQuery.error ? (
					<p className="text-sm text-red-600" role="alert">
						{listQuery.error.message}
					</p>
				) : items.length === 0 ? (
					<p
						className="text-sm text-zinc-500"
						data-testid="top-advertisers-widget-empty"
					>
						No advertisers in this range.
					</p>
				) : (
					<div className="overflow-x-auto">
						<table
							className="w-full border-collapse"
							data-testid="top-advertisers-widget-table"
						>
							<thead className="bg-zinc-50">
								<tr>
									<th className={TH}>Rank</th>
									<th className={TH}>Advertiser</th>
									<th className={`${TH} text-right`}>Spend</th>
									<th className={`${TH} text-right`}>Conversions</th>
									<th className={`${TH} text-right`}>CR</th>
									<th className={`${TH} text-right`}>ROAS</th>
									<th className={TH}>Trend</th>
								</tr>
							</thead>
							<tbody className="divide-y divide-zinc-200">
								{items.map((row) => (
									<tr
										key={row.id}
										data-testid={`top-advertisers-widget-row-${row.id}`}
									>
										<td className={`${TD} font-semibold text-zinc-700`}>
											#{row.rank}
										</td>
										<td className={`${TD} font-medium`}>{row.advertiser}</td>
										<td className={`${TD} text-right tabular-nums`}>
											{fmtMoney(row.spend ?? 0)}
										</td>
										<td className={`${TD} text-right tabular-nums`}>
											{fmtInt(row.conversions ?? 0)}
										</td>
										<td className={`${TD} text-right tabular-nums`}>
											{fmtPct(row.cr ?? 0)}
										</td>
										<td className={`${TD} text-right tabular-nums`}>
											{fmtRoas(row.roas ?? 0)}
										</td>
										<td className={TD}>
											<Sparkline values={row.trend ?? []} />
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				)}
				<div className="mt-4 flex justify-end">
					<a
						href="/advertisers"
						className="text-sm font-medium text-emerald-700 hover:text-emerald-800"
						data-testid="top-advertisers-widget-view-all"
					>
						View all advertisers &rarr;
					</a>
				</div>
			</section>
		</main>
	);
}

export default TopAdvertisersWidgetPage;
